import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const EnlargedView = ({ image, onClose, onAddToFavorites, favorited }) => {
  // Add ref for the modal content
  const modalContentRef = useRef(null);
  const imageRefs = useRef([]);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchLatestXRef = useRef(0);
  const touchLatestYRef = useRef(0);

  // Parse image URLs into an array - updated for new Algolia structure
  const imageUrls = React.useMemo(() => {
    // Check for new img array field first
    if (image.img && Array.isArray(image.img)) {
      return image.img.map((url) => applyZenmarketProxy(url));
    }
    // Fallback to allImages for backward compatibility
    if (image.allImages && Array.isArray(image.allImages)) {
      return image.allImages.map((url) => applyZenmarketProxy(url));
    } else if (image.pictureUrl && typeof image.pictureUrl === "string") {
      return image.pictureUrl.includes(",")
        ? image.pictureUrl
            .split(",")
            .map((url) => applyZenmarketProxy(url.trim()))
        : [applyZenmarketProxy(image.pictureUrl)];
    } else {
      return [image.pictureUrl || ""];
    }
  }, [image.img, image.allImages, image.pictureUrl]);

  // State to track current image index
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(new Set([0])); // Start with first image assumed loaded
  const [currentImageLoading, setCurrentImageLoading] = useState(false);
  const hasMultipleImages = imageUrls.length > 1;

  // Play the close animation before unmounting. handleClose triggers the
  // reverse animation; once it finishes we actually unmount via onClose.
  const [isClosing, setIsClosing] = useState(false);
  const handleClose = useCallback(() => setIsClosing(true), []);

  useEffect(() => {
    if (!isClosing) return undefined;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const timer = setTimeout(onClose, prefersReducedMotion ? 0 : 280);
    return () => clearTimeout(timer);
  }, [isClosing, onClose]);

  // Preload images for better performance
  useEffect(() => {
    imageUrls.forEach((url, index) => {
      if (url && !imagesLoaded.has(index)) {
        const img = new Image();
        img.onload = () => {
          setImagesLoaded((prev) => new Set([...prev, index]));
          if (index === currentIndex) {
            setCurrentImageLoading(false);
          }
        };
        img.onerror = () => {
          // Even if image fails to load, mark it as "loaded" to prevent infinite loading
          setImagesLoaded((prev) => new Set([...prev, index]));
          if (index === currentIndex) {
            setCurrentImageLoading(false);
          }
        };
        img.src = url;
        imageRefs.current[index] = img;
      }
    });
  }, [imageUrls, imagesLoaded, currentIndex]);

  // Handle image loading state when currentIndex changes
  useEffect(() => {
    if (!imagesLoaded.has(currentIndex)) {
      setCurrentImageLoading(true);
    } else {
      setCurrentImageLoading(false);
    }
  }, [currentIndex, imagesLoaded]);

  // Handle background click to close modal
  const handleBackgroundClick = useCallback(
    (e) => {
      // Close only if clicking outside the modal content
      if (
        modalContentRef.current &&
        !modalContentRef.current.contains(e.target)
      ) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    touchLatestXRef.current = touch.clientX;
    touchLatestYRef.current = touch.clientY;
  };

  const handleTouchMove = (event) => {
    const touch = event.touches[0];
    touchLatestXRef.current = touch.clientX;
    touchLatestYRef.current = touch.clientY;
  };

  // Horizontal swipes page through the images (they no longer close the
  // preview); a swipe left advances, a swipe right goes back.
  const handleTouchEnd = () => {
    const deltaX = touchLatestXRef.current - touchStartXRef.current;
    const deltaY = touchLatestYRef.current - touchStartYRef.current;
    const isHorizontalSwipe =
      Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY);

    if (isHorizontalSwipe && hasMultipleImages) {
      if (deltaX < 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }
  };

  // Navigation functions - optimized for immediate response
  const goToNext = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setCurrentIndex((prev) => (prev + 1) % imageUrls.length);
    },
    [imageUrls.length]
  );

  const goToPrevious = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setCurrentIndex(
        (prev) => (prev - 1 + imageUrls.length) % imageUrls.length
      );
    },
    [imageUrls.length]
  );

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        goToNext();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        goToPrevious();
      } else if (e.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToNext, goToPrevious, handleClose]);

  return createPortal(
    <div
      className={
        isClosing
          ? "preview-overlay preview-overlay--closing"
          : "preview-overlay"
      }
      onClick={handleBackgroundClick}
    >
      <div
        className={
          isClosing ? "preview-modal preview-modal--closing" : "preview-modal"
        }
        ref={modalContentRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">{image.title}</h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X size={24} />
          </button>
        </div>

        <div
          className="relative flex items-center justify-center bg-gray-50"
          style={{ minHeight: "400px", maxHeight: "70vh" }}
        >
          <img
            src={imageUrls[currentIndex]}
            alt={`${image.title} - ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain transition-opacity duration-200"
            style={{
              maxHeight: "60vh",
              opacity: currentImageLoading ? 0.3 : 1,
            }}
            loading="eager"
            onLoad={() => {
              setImagesLoaded((prev) => new Set([...prev, currentIndex]));
              setCurrentImageLoading(false);
            }}
            onError={() => {
              setImagesLoaded((prev) => new Set([...prev, currentIndex]));
              setCurrentImageLoading(false);
            }}
          />

          {/* Loading indicator for current image */}
          {currentImageLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          )}

          {/* Image counter if multiple images */}
          {hasMultipleImages && (
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-2 py-1 rounded-md">
              {currentIndex + 1} / {imageUrls.length}
            </div>
          )}

          {/* Navigation arrows only if multiple images */}
          {hasMultipleImages && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 focus:outline-none transition-all duration-150 active:scale-95"
                aria-label="Previous image"
                type="button"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 focus:outline-none transition-all duration-150 active:scale-95"
                aria-label="Next image"
                type="button"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
        </div>

        <div className="p-4">
          <a href={image.url} className="text-blue-600 hover:underline">
            View original listing
            {image.shop && ` at ${image.shop.toUpperCase()}`}
          </a>
          <div className="flex justify-between items-center mt-2">
            <p className="text-gray-600">Size: {image.size}</p>
            {/* <button
              className="p-2 text-black rounded-lg hover:bg-blue-700"
              onClick={onAddToFavorites}
            >
              {favorited ? "Favorited" : "Favorite"}
            </button> */}
          </div>
          <div className="flex justify-between items-center mt-2">
            <p className="text-gray-600">Price: {image.price}</p>
            <p className="text-gray-600 text-sm">
              Listed:{" "}
              {image.createdAt
                ? new Date(image.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "Unknown"}
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Helper function to apply zenmarket proxy to URLs
const applyZenmarketProxy = (url) => {
  try {
    // Early return if url is not a string or is empty
    if (!url) return "";
    if (typeof url !== "string") return String(url || "");

    // Trim the URL and replace the domain pattern
    const trimmedUrl = url.trim();

    // Replace both full domain patterns
    // First pattern: images.auctions.yahoo.co.jp
    // Second pattern: auctions.c.yimg.jp
    return trimmedUrl
      .replace(/https?:\/\/auctions\.c\.yimg\.jp/g, "https://ys.zenmarket.jp")
      .replace(
        /https?:\/\/images\.auctions\.yahoo\.co\.jp/g,
        "https://ys.zenmarket.jp"
      );
  } catch (error) {
    console.error("Error in applyZenmarketProxy:", error, "URL:", url);
    return url || "";
  }
};

export const Listing = ({
  title,
  price,
  size,
  pictureUrl,
  allImages,
  img, // Add img prop for new Algolia structure
  url,
  onAddToFavorites,
  id,
  favorited,
  objectID, // Add objectID for LookingSimilar
  createdAt, // Add createdAt prop
  shop, // Add shop prop
}) => {
  const [selectedImage, setSelectedImage] = useState(null);

  useLayoutEffect(() => {
    if (!selectedImage) return;

    // Lock scrolling without moving anything: `overflow-y: scroll` on <body>
    // propagates to the viewport, so toggling it to hidden freezes the page in
    // place. We only compensate for the scrollbar width (desktop), which avoids
    // the layout shift / flash that pinning `position: fixed` used to cause on
    // mobile. The fixed, blurred overlay covers the page behind it.
    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;

    document.body.style.overflow = "hidden";
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [selectedImage]);

  const handleImageClick = () => {
    setSelectedImage({
      title,
      pictureUrl,
      allImages,
      img: img || allImages, // Use img array if available, fallback to allImages
      description: `${title} - ${size}`,
      size,
      price,
      url,
      objectID: objectID || id, // Pass objectID for LookingSimilar
      createdAt, // Pass createdAt to the enlarged view
      shop, // Pass shop to the enlarged view
    });
  };

  // Get the first image URL - updated for new Algolia structure
  const firstImageUrl = (() => {
    // Check for new img array field first
    if (img && Array.isArray(img) && img.length > 0) {
      return img[0];
    }
    // Fallback to allImages array
    if (allImages && Array.isArray(allImages) && allImages.length > 0) {
      return allImages[0];
    }
    // Fallback to pictureUrl handling
    if (Array.isArray(pictureUrl)) {
      return pictureUrl[0];
    } else if (pictureUrl && pictureUrl.includes(",")) {
      return pictureUrl.split(",")[0].trim();
    }
    return pictureUrl;
  })();

  // Apply proxy to the first image URL only
  const proxiedFirstImageUrl = applyZenmarketProxy(firstImageUrl);

  return (
    <div className="listing">
      <img
        className="listing__picture cursor-pointer"
        src={proxiedFirstImageUrl}
        alt={title}
        onClick={handleImageClick}
      />
      <div className="listing__text">
        <a href={url} className="listing__title">
          {title}
        </a>
        <p className="listing__size">{size}</p>
        <p className="listing__price">{price}</p>
      </div>
      {/* <button className="listing__add-to-favorites" onClick={onAddToFavorites}>
        {favorited ? "favorited" : "favorite"}
      </button> */}

      {selectedImage && (
        <EnlargedView
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
          onAddToFavorites={onAddToFavorites}
          favorited={favorited}
        />
      )}
    </div>
  );
};
